import { memo, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { classNames } from "helpers";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";

import { updateListingChannelAPI } from "services/api/sales/listings/api";
import { VehicleListing, VehicleListingChannel } from "services/api/sales/listings/types";
import { CHANNEL_CONFIG, CHANNEL_SORT_ORDER, DEFAULT_CHANNEL_CONFIG } from "services/api/sales/listings/consts";

export type ChannelToggleMode = "pause" | "activate";

interface ChannelToggleModalProps {
  show: boolean;
  mode: ChannelToggleMode;
  listing: VehicleListing;
  /** Kanali kojima se smije mijenjati stanje — bez onih s neinstaliranom integracijom. */
  channelIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

// Modal za pause/activate akciju po kanalima. U "pause" modu lista samo
// trenutno aktivne kanale (odabir = kanali koji će se pauzirati), u
// "activate" modu samo trenutno neaktivne (odabir = kanali za aktivaciju).
// Po default-u su SVI ponuđeni kanali odabrani — UX-om identično ranijem
// bulk gumbu, ali sada s mogućnošću selektivnog odznačavanja. Marketplace se
// smije pauzirati kao i svaki drugi kanal (oglas tada nigdje nije objavljen).
const ChannelToggleModal: React.FC<ChannelToggleModalProps> = (props) => {
  const { show, mode, listing, channelIds, onClose, onSuccess } = props;

  // Kanali koji se nude u listi — ovisi o modu; marketplace se ponaša kao i ostali.
  // Oglasnici s neinstaliranom integracijom uopće se ne nude.
  const candidates: VehicleListingChannel[] = useMemo(() => {
    const filter = mode === "pause" ? (c: VehicleListingChannel) => c.isActive : (c: VehicleListingChannel) => !c.isActive;
    return [...listing.channels]
      .filter((c) => channelIds.includes(c.id))
      .filter(filter)
      .sort((a, b) =>
        (CHANNEL_SORT_ORDER[a.salesChannelCode ?? ""] ?? 99) -
        (CHANNEL_SORT_ORDER[b.salesChannelCode ?? ""] ?? 99)
      );
  }, [listing.channels, channelIds, mode]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Inicijalno označi sve kandidate svaki put kad se modal otvori.
  useEffect(() => {
    if (show) setSelectedIds(candidates.map((c) => c.id));
  }, [show, candidates]);

  const toggle = (channelId: string) => {
    setSelectedIds((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    setIsLoading(true);
    try {
      const targetIsActive = mode === "activate";
      // Sekvencijalno gađa per-channel endpoint. Svaki poziv postavlja
      // isActive na željenu vrijednost (true za activate, false za pause)
      // i čuva postojeće override-e (jer ih BE ne dira u ovom requestu).
      for (const channelId of selectedIds) {
        const ch = listing.channels.find((c) => c.id === channelId);
        if (!ch) continue;
        await updateListingChannelAPI(listing.id, channelId, {
          isActive: targetIsActive,
          titleOverride: ch.titleOverride ?? null,
          priceOverride: ch.priceOverride ?? null,
          descriptionOverride: ch.descriptionOverride ?? null,
        });
      }
      onSuccess();
    } catch {
      // greške rukuje createApiCall (snackbar)
    }
    setIsLoading(false);
  };

  const titleMap: Record<ChannelToggleMode, string> = {
    pause: "Pauziraj oglas",
    activate: "Aktiviraj oglas",
  };

  const descriptionMap: Record<ChannelToggleMode, string> = {
    pause: "Odaberite oglasnike na kojima želite pauzirati oglas.",
    activate: "Odaberite oglasnike na kojima želite aktivirati oglas.",
  };

  const emptyMap: Record<ChannelToggleMode, string> = {
    pause: "Nema aktivnih oglasnika koje bi se moglo pauzirati.",
    activate: "Svi oglasnici su već aktivni.",
  };

  const confirmLabel = mode === "pause" ? "Pauziraj odabrane" : "Aktiviraj odabrane";
  const confirmVariant = mode === "pause" ? "warning" : "success";

  return (
    <Modal show={show} onHide={onClose} size="small" position="centered">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={titleMap[mode]}
        onHide={onClose}
      />
      <ModalBody>
        <div className="p-5">
          <p className="text-sm text-slate-500 mb-4">{descriptionMap[mode]}</p>

          {candidates.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6">{emptyMap[mode]}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
              {candidates.map((ch) => {
                const cfg = CHANNEL_CONFIG[ch.salesChannelCode ?? ""] || DEFAULT_CHANNEL_CONFIG;
                const checked = selectedIds.includes(ch.id);
                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => toggle(ch.id)}
                    className={classNames(
                      "relative flex items-center gap-3 p-4 rounded-sm border transition-all text-left cursor-pointer",
                      checked
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    )}
                  >
                    <span
                      className={classNames(
                        "flex items-center justify-center w-10 h-10 rounded-sm flex-shrink-0",
                        cfg.badge.bg,
                        cfg.badge.text
                      )}
                    >
                      <ReactSVG src={cfg.icon} className="w-5 h-5 [&_svg]:w-5 [&_svg]:h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800">{ch.salesChannelName}</div>
                    </div>
                    <span
                      className={classNames(
                        "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                        checked ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300 bg-white"
                      )}
                    >
                      {checked && (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2 px-5 py-3">
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Odustani
          </Button>
          <Button
            variant={confirmVariant}
            startIcon={mode === "pause" ? "/media/icons/pause.svg" : "/media/icons/play.svg"}
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || candidates.length === 0}
            loading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(ChannelToggleModal);
